import { Helmet } from "react-helmet-async";

const SITE_NAME = "People Who Matter";
const DEFAULT_DESCRIPTION = "People Who Matter is a new age, technology enabled digital editorial platform focused on the people, brands, companies and initiatives shaping business, culture and contemporary India.";
const DEFAULT_IMAGE = "/hero-banner.png";
const SITE_URL = "https://peoplewhomatter.in";

// Single source of truth for the publisher entity used in every JSON-LD block (same @id everywhere).
export const ORG_ID = `${SITE_URL}/#organization`;
export const WEBSITE_ID = `${SITE_URL}/#website`;
export const ORGANIZATION = {
  "@type": "Organization",
  "@id": ORG_ID,
  name: SITE_NAME,
  alternateName: "PWM",
  url: SITE_URL,
  description: DEFAULT_DESCRIPTION,
  email: "hello@peoplewhomatter.in",
  logo: { "@type": "ImageObject", url: `${SITE_URL}/logo-black.png`, width: 800, height: 120 },
  sameAs: ["https://twitter.com/PwM_Media", "https://www.linkedin.com/company/people-who-matter", "https://www.instagram.com/peoplewhomatter.in"],
  contactPoint: { "@type": "ContactPoint", contactType: "editorial", email: "hello@peoplewhomatter.in", url: `${SITE_URL}/contact` },
};
export { SITE_URL };

const TITLE_MAX = 60;
const DESC_MAX = 160;

// "Headline | People Who Matter" when it fits in a search result; the headline alone when it would be cut off.
export const pageTitleFor = (title) => {
  if (!title) return `${SITE_NAME}, Voices of Influence`;
  if (title.includes(SITE_NAME)) return title;
  const full = `${title} | ${SITE_NAME}`;
  return full.length <= TITLE_MAX ? full : title;
};

// Meta descriptions are trimmed at a word boundary; the visible excerpt on the page is untouched.
export const trimDescription = (text, limit = DESC_MAX) => {
  const clean = (text || "").replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
  if (clean.length <= limit) return clean;
  return clean.slice(0, limit - 1).replace(/\s+\S*$/, "").replace(/[\s,;:-]+$/, "") + "…";
};

// Share-card derivative: JPEG, ≤1200px wide, ratio preserved (link scrapers reject multi-MB PNGs; some ignore WebP).
export const shareImage = (url) => {
  if (!url || !url.includes("res.cloudinary.com") || !url.includes("/upload/") || /\/upload\/[a-z_0-9,:]*(f_|w_)/.test(url)) return url;
  return url.replace("/upload/", "/upload/f_jpg,q_auto:good,c_limit,w_1200/");
};

const SEO = ({
  title,
  description: rawDescription = DEFAULT_DESCRIPTION,
  image = DEFAULT_IMAGE,
  imageAlt = null,
  url,
  type = "website",
  article = null,
  noindex = false,
  jsonLd = null,
  rawTitle = null,
}) => {
  const pageTitle = rawTitle || pageTitleFor(title);
  const description = trimDescription(rawDescription) || DEFAULT_DESCRIPTION;
  const shared = shareImage(image);
  const fullImage = shared?.startsWith("http") ? shared : `${SITE_URL}${shared}`;
  const fullUrl = url ? `${SITE_URL}${url}` : SITE_URL;
  const alt = imageAlt || title || SITE_NAME;
  const schemas = jsonLd ? (Array.isArray(jsonLd) ? jsonLd : [jsonLd]) : [];

  return (
    <Helmet>
      <title>{pageTitle}</title>
      <meta name="description" content={description} />
      {noindex === "follow" ? (
        // Real page with nothing to index yet (e.g. empty section): keep it out of the index but let links pass.
        <meta name="robots" content="noindex, follow" />
      ) : noindex ? (
        <meta name="robots" content="noindex, nofollow, noarchive" />
      ) : (
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
      )}
      {noindex === true && <meta name="googlebot" content="noindex, nofollow" />}
      {noindex === true && <meta name="prerender-status-code" content="404" />}

      {/* Open Graph */}
      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={fullImage} />
      <meta property="og:image:alt" content={alt} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:url" content={fullUrl} />
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content="en_IN" />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content="@PwM_Media" />
      <meta name="twitter:title" content={pageTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={fullImage} />
      <meta name="twitter:image:alt" content={alt} />

      {/* Article-specific */}
      {article && (
        <>
          <meta property="article:published_time" content={article.published_at} />
          {article.updated_at && <meta property="article:modified_time" content={article.updated_at} />}
          <meta property="article:author" content={article.author} />
          <meta property="article:section" content={article.category} />
          {article.tags?.map((tag) => (
            <meta key={tag} property="article:tag" content={tag} />
          ))}
        </>
      )}

      {/* Canonical (omitted on noindex pages so crawlers get one clear signal) */}
      {!noindex && <link rel="canonical" href={fullUrl} />}

      {/* JSON-LD structured data */}
      {schemas.map((s, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(s)}
        </script>
      ))}
    </Helmet>
  );
};

export default SEO;
